import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

import { CheckExpandable } from '@/components/CheckExpandable';
import { SectionHeader } from '@/components/SectionHeader';
import { useFormSyncState } from '@/hooks';
import { Button, Card, Form, Grid, Input, InputNumber, Switch } from '@arco-design/web-react';
import { IconDelete, IconPlus } from '@arco-design/web-react/icon';
import { useEffect } from 'react';
import styled from 'styled-components';
import { GrayBox, ItemTitle } from '../tools';
import { SecurityContextData, useSecurityContextStore } from './useSecurityContextStore';

const t = i18n.t.bind(i18n);
const FormItem = Form.Item;
const FormList = Form.List;
const Row = Grid.Row;
const Col = Grid.Col;

const initialValues: SecurityContextData = {};

const msg = {
  container_security_context_title: t('components:container.security.context'),
  container_security_context_description: t('components:customize.container.security.settings'),
  access_control: t('components:access.control'),
  privileged_mode: t('components:privileged.mode'),
  privileged_mode_desc: t('components:allow.containers.to.access.host.s.privileged.operations'),
  allow_privilege_escalation: t('components:allow.privilege.escalation'),
  allow_privilege_escalation_desc: t(
    'components:allow.container.processes.to.gain.more.privileges.than.parent.processes.this.option.is.enabled.by.default.when.privileged.mode.is.enabled',
  ),
  read_only_root_filesystem: t('components:read.only.root.filesystem'),
  read_only_root_filesystem_desc: t(
    'components:set.container.filesystem.root.directory.as.read.only',
  ),
  user_and_group: t('components:user.and.group'),
  run_as_non_root: t('components:only.allow.non.root.users.to.run'),
  run_as_non_root_desc: t(
    'components:check.if.the.container.will.run.as.root.before.starting.it.if.the.container.will.run.as.root.do.not.start.it',
  ),
  run_as_user: t('common:user'),
  run_as_user_placeholder: t(
    'components:uid.of.the.container.process.entry.point.defaults.to.uid.specified.in.image.metadata',
  ),
  run_as_group: t('common:user.group'),
  run_as_group_placeholder: t(
    'components:gid.of.the.container.process.entry.point.defaults.to.the.runtime.s.default.gid',
  ),
  selinux_context: t('components:selinux.context'),
  level: t('components:level'),
  role: t('common:role'),
  type: t('common:type'),
  user: t('common:user'),
  capabilities: t('components:capabilities'),
  add: t('common:add'),
  drop: t('common:remove'),
};

export function ContainerSecurityContext({ visible }: { visible?: boolean }) {
  const { t } = useTranslation();
  const [form] = Form.useForm<SecurityContextData>();

  const {
    isSecurityContextSelected,
    securityContextData,
    toggleSecurityContextSelected,
    updateSecurityContextData,
  } = useSecurityContextStore();
  useFormSyncState(form, securityContextData);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [form, visible]);

  const Header = (
    <SectionHeader
      inline
      title={msg.container_security_context_title || t('components:container.security.context')}
      description={
        msg.container_security_context_description ||
        t('components:customize.container.security.settings')
      }
    />
  );

  return (
    <>
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={(_, values) => {
          updateSecurityContextData(values);
        }}
        layout="vertical"
      >
        <Card>
          <CheckExpandable
            label={Header}
            checked={isSecurityContextSelected}
            onChange={toggleSecurityContextSelected}
          >
            <Content>
              <GrayBox>
                <ItemTitle>{msg.access_control}</ItemTitle>
                <Label>
                  <FormItem field="privileged" triggerPropName="checked">
                    <Switch />
                  </FormItem>
                  <LabelItem>
                    <span>{msg.privileged_mode}</span>
                    <p>{msg.privileged_mode_desc}</p>
                  </LabelItem>
                </Label>

                <Label>
                  <FormItem field="allowPrivilegeEscalation" triggerPropName="checked">
                    <Switch />
                  </FormItem>
                  <LabelItem>
                    <span>{msg.allow_privilege_escalation}</span>
                    <p>{msg.allow_privilege_escalation_desc}</p>
                  </LabelItem>
                </Label>
                <Label>
                  <FormItem field="readOnlyRootFilesystem">
                    <Switch />
                  </FormItem>
                  <LabelItem>
                    <span>{msg.read_only_root_filesystem}</span>
                    <p>{msg.read_only_root_filesystem_desc}</p>
                  </LabelItem>
                </Label>
              </GrayBox>
              <GrayBox>
                <ItemTitle>{msg.user_and_group}</ItemTitle>
                <Label>
                  <FormItem field="runAsNonRoot">
                    <Switch />
                  </FormItem>
                  <LabelItem>
                    <span>{msg.run_as_non_root}</span>
                    <p>{msg.run_as_non_root_desc}</p>
                  </LabelItem>
                </Label>
                <FormItem field="runAsUser" layout="vertical" label={msg.run_as_user}>
                  <InputNumber placeholder={msg.run_as_user_placeholder} hideControl />
                </FormItem>
                <FormItem field="runAsGroup" layout="vertical" label={msg.run_as_group}>
                  <InputNumber placeholder={msg.run_as_group_placeholder} hideControl />
                </FormItem>
              </GrayBox>
              <GrayBox>
                <ItemTitle>{msg.selinux_context}</ItemTitle>
                <Row gutter={8} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.level" label={msg.level}>
                      <Input />
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.role" label={msg.role}>
                      <Input />
                    </FormItem>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.type" label={msg.type}>
                      <Input />
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem field="seLinuxOptions.user" label={msg.user}>
                      <Input />
                    </FormItem>
                  </Col>
                </Row>
              </GrayBox>
              <GrayBox>
                <ItemTitle>{msg.capabilities}</ItemTitle>
                <FormItem label={msg.add}>
                  <FormList field="capabilities.add">
                    {(fields, { add, remove, move }) => {
                      return (
                        <div>
                          {fields.map((item, index) => {
                            return (
                              <CapabilityItem key={item.key}>
                                <FormItem
                                  field={item.field}
                                  rules={[
                                    {
                                      required: true,
                                    },
                                  ]}
                                >
                                  <Input />
                                </FormItem>

                                <Button
                                  icon={<IconDelete />}
                                  type="text"
                                  status="danger"
                                  onClick={() => remove(index)}
                                />
                              </CapabilityItem>
                            );
                          })}
                          <Button
                            icon={<IconPlus />}
                            type="primary"
                            size="mini"
                            onClick={() => {
                              add();
                            }}
                          >
                            {msg.add}
                          </Button>
                        </div>
                      );
                    }}
                  </FormList>
                </FormItem>
                <FormItem label={msg.drop}>
                  <FormList field="capabilities.drop">
                    {(fields, { add, remove, move }) => {
                      return (
                        <div>
                          {fields.map((item, index) => {
                            return (
                              <CapabilityItem key={item.key}>
                                <FormItem
                                  field={item.field}
                                  rules={[
                                    {
                                      required: true,
                                    },
                                  ]}
                                >
                                  <Input />
                                </FormItem>

                                <Button
                                  icon={<IconDelete />}
                                  type="text"
                                  status="danger"
                                  onClick={() => remove(index)}
                                />
                              </CapabilityItem>
                            );
                          })}
                          <Button
                            icon={<IconPlus />}
                            type="primary"
                            size="mini"
                            onClick={() => {
                              add();
                            }}
                          >
                            {msg.add}
                          </Button>
                        </div>
                      );
                    }}
                  </FormList>
                </FormItem>
              </GrayBox>
            </Content>
          </CheckExpandable>
        </Card>
      </Form>
    </>
  );
}

/** ============================ 样式 ==================================== */
const Content = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 8px;
  .arco-form-item {
    margin-bottom: 0;
    width: auto; // 修复 Switch 宽度问题
  }
`;

const LabelItem = styled.div`
  display: flex;
  flex-direction: column;
  color: #000;
  p {
    font-size: 12px;
    opacity: 0.5;
  }
`;

const CapabilityItem = styled.div`
  display: flex;
  gap: 8px;
`;
